<script setup lang="ts">
import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { baseServicePricesApi } from '@/api/base-service-prices';
import { formatCurrency } from '@/utils/currency';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import BaseServicePriceForm from './base-service-price-form.vue';

const { data: baseServicePrices, isLoading } = useQuery({
  queryKey: ['baseServicePrices'],
  queryFn: baseServicePricesApi.index,
  initialData: [],
});

const { hasPermission } = usePermissions();
const { tableProps } = useResponsiveTable();

const headers: { title: string, key: string, sortable?: boolean }[] = [
  { title: 'Servicio', key: 'humanServiceType' },
  { title: 'Precio Semana', key: 'weekPrice' },
  { title: 'Pago Semana (líquido)', key: 'weekCost' },
  { title: 'Precio Fin de Semana', key: 'weekendPrice' },
  { title: 'Pago Fin de Semana (líquido)', key: 'weekendCost' },
  { title: 'Con boleta de honorarios', key: 'withTaxBill' },
];

const hasActions = computed(() =>
  hasPermission.value('service_prices', 'update'),
);

if (hasActions.value) {
  headers.push({ title: 'Acciones', key: 'actions', sortable: false });
}

</script>
<template>
  <v-card
    v-if="hasPermission('service_prices', 'index')"
  >
    <v-card-title class="d-flex justify-space-between align-center">
      <span>Precios Base de Servicios</span>
      <v-dialog
        max-width="800"
        scrollable
      >
        <template #activator="{ props: activatorProps }">
          <v-btn
            v-if="hasPermission('service_prices', 'create')"
            color="primary"
            v-bind="activatorProps"
          >
            Agregar
          </v-btn>
        </template>
        <template #default="{ isActive }">
          <base-service-price-form
            @close="isActive.value = false"
          />
        </template>
      </v-dialog>
    </v-card-title>

    <v-card-text>
      <v-data-table
        v-bind="tableProps"
        :headers="headers"
        :items="baseServicePrices"
        :loading="isLoading"
        class="mt-8"
        loading-text="Cargando los precios base de servicios..."
        no-data-text="No hay precios base de servicios creados"
      >
        <template #[`item.weekPrice`]="{ item }">
          {{ formatCurrency(item.weekPrice) }}
        </template>
        <template #[`item.weekCost`]="{ item }">
          {{ formatCurrency(item.weekCost) }}
        </template>
        <template #[`item.weekendPrice`]="{ item }">
          {{ formatCurrency(item.weekendPrice) }}
        </template>
        <template #[`item.weekendCost`]="{ item }">
          {{ formatCurrency(item.weekendCost) }}
        </template>
        <template #[`item.withTaxBill`]="{ item }">
          <v-chip :color="item.withTaxBill ? 'green' : 'red'">
            {{ item.withTaxBill ? 'Sí' : 'No' }}
          </v-chip>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-dialog
            max-width="800"
            scrollable
          >
            <template #activator="{ props: activatorProps }">
              <v-btn
                v-if="hasPermission('service_prices', 'update')"
                v-bind="activatorProps"
                icon="mdi-pencil"
                size="small"
                class="me-2"
              />
            </template>
            <template #default="{ isActive }">
              <base-service-price-form
                :base-service-price="item"
                @close="isActive.value = false"
              />
            </template>
          </v-dialog>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>
